import { FC, ReactNode, useState } from "react";
import { Drawer, DrawerProps } from "antd";
import { useDrag } from "@use-gesture/react";
import SheetHeader from "../SheetHeader/SheetHeader";
import { FLICK_VELOCITY, SNAP_RATIOS, SnapSize } from "./constants";
import { resolveRelease, toDvh } from "./utils";

export type CarrierHelpers = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

export type CarrierProps = {
  renderTrigger: (helpers: CarrierHelpers) => ReactNode;
  renderSheet: (helpers: CarrierHelpers) => ReactNode;
  label?: ReactNode;
  showTitle?: boolean;
} & Partial<DrawerProps>;

const Carrier: FC<CarrierProps> = ({
  renderTrigger,
  renderSheet,
  className,
  classNames,
  styles,
  label,
  showTitle = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [size, setSize] = useState<SnapSize>("mid");
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const open = () => {
    setDragHeight(null);
    setIsOpen(true);
  };
  const close = () => setIsOpen(false);
  const helpers: CarrierHelpers = { isOpen, open, close };

  const bindHandleDrag = useDrag(
    ({ last, memo, movement: [, my], velocity: [, vy], direction: [, dy] }) => {
      const viewport = window.innerHeight;
      const startHeight: number = memo ?? viewport * SNAP_RATIOS[size];
      const height = Math.min(Math.max(startHeight - my, 0), viewport);

      if (!last) {
        setIsDragging(true);
        setDragHeight(height);
        return startHeight;
      }

      setIsDragging(false);
      const target = resolveRelease({ size, height, viewport, isFlick: vy > FLICK_VELOCITY, isDown: dy > 0 });
      if (target === "closed") return close();

      setDragHeight(null);
      setSize(target);
    },
    { axis: "y", filterTaps: true, enabled: isOpen },
  );

  return (
    <>
      {renderTrigger(helpers)}
      <Drawer
        placement="bottom"
        forceRender
        closeIcon={null}
        focusable={{ focusTriggerAfterClose: false }}
        title={
          <div className="flex w-full touch-none flex-col items-center justify-center gap-3 pt-2" {...bindHandleDrag()}>
            <div className="bg-border-hover h-1 w-10 rounded-full"></div>
            {showTitle && (
              <SheetHeader label={label} onClose={close} labelClassName="text-lg font-semibold" closeClassName="bg-transparent" />
            )}
          </div>
        }
        className={["p-0! rounded-t-2xl!", className].filter(Boolean).join(" ")}
        open={isOpen}
        onClose={close}
        size={dragHeight ?? toDvh(size)}
        classNames={{
          body: "p-0! flex! flex-col! w-full!",
          header: "p-0! border-b-0!",
          ...classNames,
        }}
        styles={{ ...styles }}
        {...props}
      >
        {renderSheet(helpers)}
      </Drawer>
    </>
  );
};

export default Carrier;
